import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import { FloatingPopup } from '@nx-frontend/shared/ui/floating-popup';
import { Tabs } from '@nx-frontend/shared/ui/tabs';
import { generalEnv } from '@nx-frontend/shared/env';
import { hideAiHelperEvent } from '@nx-frontend/shared/store/ai-helper';
import { PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import {
  $aiHelperAgent,
  $chatbotAgentModeMainTabState,
  abortAgentMessageEvent,
  AgentModeMainTabState,
  loadAIHelperAgentEvent,
  resetAIHelperAgentEvent,
  setChatbotAgentModeMainTabStateEvent,
} from './agent-store';
import { ChatbotAgentHistory } from './chatbot-agent-history';
import { ChatbotAgentModeMainTab } from './chatbot-agent-mode-main-tab';
import { ChatbotContext } from './chatbot-context';
import { ChatbotHeader } from './chatbot-header';
import { $activeThreadId, $writingMessage, setActiveThreadIdEvent } from './store';

interface ChatbotAgentModeProps {
  /**
   * if not set then true
   */
  backButton?: boolean;

  /**
   * if not set then true
   */
  closeButton?: boolean;

  /**
   * if not set then true
   */
  tabs?: boolean;

  fullScreenButton?: boolean;
  noInputLineMode?: boolean;
  isPortal?: boolean;

  /**
   * if set, the start screen will be shown despite all other conditions
   */
  forceStartView?: boolean;

  /**
   * if set and forceStartView is not, then agents without inputs will be run automatically on opening
   */
  autoRunIfPossible?: boolean;
}

export const ChatbotAgentMode: FC<ChatbotAgentModeProps> = (props: ChatbotAgentModeProps) => {
  const { backButton = true, closeButton = true, tabs = true, isPortal, fullScreenButton } = props;

  const [activeThreadId, aiHelperAgent, chatbotAgentModeMainTabState, writingMessage] = useUnit([
    $activeThreadId,
    $aiHelperAgent,
    $chatbotAgentModeMainTabState,
    $writingMessage,
  ]);
  const [
    hideAiHelper,
    setActiveThreadId,
    resetAIHelperAgent,
    loadAIHelperAgent,
    setChatbotAgentModeMainTabState,
    abortAgentMessage,
  ] = useUnit([
    hideAiHelperEvent,
    setActiveThreadIdEvent,
    resetAIHelperAgentEvent,
    loadAIHelperAgentEvent,
    setChatbotAgentModeMainTabStateEvent,
    abortAgentMessageEvent,
  ]);
  const aiContext = useContext(ChatbotContext);

  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [openExistingThread, setOpenExistingThread] = useState<boolean>(aiContext.openExistingAgentThread ?? false);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const aiAgentStartedLoading = useRef<boolean>();

  const { agent } = aiHelperAgent;

  // Cleaning previous agent
  useEffect(() => {
    if (aiContext.agentId && !openExistingThread) {
      resetAIHelperAgent();
      aiAgentStartedLoading.current = false;
      setActiveThreadId(null);
    }
  }, [aiContext.agentId, openExistingThread, resetAIHelperAgent, setActiveThreadId]);

  // Set default state for main tab
  useEffect(() => {
    setChatbotAgentModeMainTabState(AgentModeMainTabState.LOADING);
    return () => {
      setChatbotAgentModeMainTabState(AgentModeMainTabState.LOADING);
    };
  }, [setChatbotAgentModeMainTabState]);

  // Load agent
  useEffect(() => {
    if (
      !aiAgentStartedLoading.current &&
      (aiHelperAgent.state === 'not_loaded' ||
        (aiHelperAgent?.agent?.globalId !== aiContext.agentId && aiContext.agentId))
    ) {
      aiAgentStartedLoading.current = true;
      console.assert(aiContext.orgId, 'orgId is required to be in the aiContext');

      loadAIHelperAgent({
        globalId: aiContext.agentId as string,
        orgId: aiContext.orgId as string,
      });
    }
  }, [aiContext.agentId, aiContext.orgId, aiHelperAgent?.agent?.globalId, aiHelperAgent.state, loadAIHelperAgent]);

  const agentLoaded = useMemo(() => {
    return aiHelperAgent.state === 'loaded' && aiHelperAgent?.agent?.globalId === aiContext.agentId;
  }, [aiContext.agentId, aiHelperAgent.agent?.globalId, aiHelperAgent.state]);

  const agentInputsExist = useMemo(() => {
    return !agentLoaded || Boolean(agent && agent?.inputs.length);
  }, [agent, agentLoaded]);

  const showTabs = tabs && !isPortal;

  const showBackButton = useMemo(() => {
    if (!backButton) {
      return false;
    }
    return chatbotAgentModeMainTabState === AgentModeMainTabState.CHAT || currentTab === 1;
  }, [backButton, chatbotAgentModeMainTabState, currentTab]);

  const showStartView = props.forceStartView || (agentLoaded && !agentInputsExist && !props.autoRunIfPossible);

  const abortMessageIfWriting = useCallback(() => {
    if (!writingMessage?.id) {
      return;
    }
    if (!aiContext.orgId) {
      return console.error('No orgId provided, fail to cancel agent message');
    }
    abortAgentMessage({
      sessionId: aiContext.sessionId,
      orgId: aiContext.orgId,
      messageId: writingMessage.id,
      text: writingMessage.message,
    });
  }, [writingMessage, aiContext.orgId, aiContext.sessionId, abortAgentMessage]);

  // stop writing message if unmounted while writing + reset thread id.
  // needed for correct behaviour on switching pages in portal
  useEffect(() => {
    return () => {
      abortMessageIfWriting();
      setActiveThreadId(null);
    };
    // This effect should be run only on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBack = () => {
    setOpenExistingThread(false);
    if (currentTab === 1) {
      setCurrentTab(0);
      return;
    }
    abortMessageIfWriting();
    setActiveThreadId(null);
  };

  const onClose = () => {
    hideAiHelper({
      apiHost: aiContext.apiHost,
      sessionId: aiContext.sessionId as string,
      workspaceId: aiContext.workspaceId,
    });
    setActiveThreadId(null);
    abortMessageIfWriting();
  };

  const onFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  const onTabChange = useCallback((tabIdx: number) => {
    setCurrentTab(tabIdx);
  }, []);

  const onHistoryItemClick = useCallback(
    (threadId: string) => {
      if (threadId) {
        setActiveThreadId(threadId);
        setOpenExistingThread(true);
        setCurrentTab(0);
      }
    },
    [setActiveThreadId]
  );

  const body = (
    <div className="flex flex-col flex-grow max-h-full h-full relative">
      <ChatbotHeader
        debugThreadId={generalEnv.env !== 'prod' && activeThreadId ? activeThreadId : undefined}
        showInfoMenu={false}
        title={agent?.title ?? ''}
        onBack={showBackButton ? onBack : undefined}
        onClose={closeButton ? onClose : undefined}
        // onFullScreen={}
        isFullscreen={isFullscreen}
        fullScreenButton={fullScreenButton}
        themeColors={aiContext.themeColors as ThemeColorSchemas}
        themeStyle={aiContext.themeStyle as PortalStyleConfig}
        onUpdateWorkspaceIndexing={(value) => {
          /* not used */
        }}
        onFullscreenToggle={onFullscreenToggle}
        isPortal={isPortal}
        hideBorder={showTabs}
      />
      {showTabs && (
        <Tabs
          className="text-sm pl-11"
          tabs={['Current', 'History']}
          onTabChange={onTabChange}
          currentTabIdx={currentTab}
        />
      )}
      <div className="grow min-h-0">
        {currentTab === 0 && (
          <ChatbotAgentModeMainTab
            agentLoaded={agentLoaded}
            agent={agent}
            openExistingAgentThread={openExistingThread}
            noInputLineMode={props.noInputLineMode}
            startWithStartScreen={showStartView}
          />
        )}
        {currentTab === 1 && (
          <div className="h-full">
            <ChatbotAgentHistory onThreadClick={onHistoryItemClick} />
          </div>
        )}
      </div>
    </div>
  );

  if (isFullscreen) {
    return (
      <FloatingPopup
        show={isFullscreen}
        position="screen_full"
        containerClassName={['flex', 'flex-col']}
        onClose={() => setIsFullscreen(false)}
        closeButton={false}
        closeByOutsideClick={false}
      >
        {body}
      </FloatingPopup>
    );
  }
  return body;
};
